/**
 * Education - Dashboard Page
 * Overview metrics for educational institutions
 */

import { useEffect, useMemo } from 'react';
import { useIndustryCourses } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GraduationCap, Users, DollarSign, TrendingUp, BookOpen, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MainLayout } from '@/components/MainLayout';

export default function EducationDashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard Educacional | Controle Financeiro';
  }, []);

  const { courses, isLoading } = useIndustryCourses({ domain: CompanyType.EDUCATION });

  const stats = useMemo(() => {
    if (!courses) return null;

    const activeCourses = courses.filter((c) => c.is_active !== false).length;
    const inactiveCourses = courses.length - activeCourses;

    // Aggregate by category
    const categoryMap = new Map<string, number>();
    courses.forEach((c) => {
      const category = (c.metadata?.category as string) || 'Outros';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    const totalCapacity = courses.reduce((sum, c) => {
      const capacity = (c.metadata?.capacity as number) || 0;
      return sum + capacity;
    }, 0);

    const totalRevenue = courses.reduce((sum, c) => {
      const price = c.price || 0;
      // Simulate enrollments for demo purposes
      const enrollments = Math.floor(Math.random() * 15) + 5;
      return sum + price * enrollments;
    }, 0);

    const avgCoursePrice = courses.length > 0 ? courses.reduce((sum, c) => sum + (c.price || 0), 0) / courses.length : 0;

    return {
      totalCourses: courses.length,
      activeCourses,
      inactiveCourses,
      categories: categoryMap.size,
      totalCapacity,
      totalRevenue,
      avgCoursePrice,
      topCategories: Array.from(categoryMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3),
    };
  }, [courses]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <GraduationCap className="h-8 w-8" />
            Dashboard Educacional
          </h1>
          <p className="text-muted-foreground">Visão geral dos seus cursos e métricas</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Total de Cursos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCourses || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.activeCourses || 0} ativos · {stats?.inactiveCourses || 0} inativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              Áreas de Conhecimento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.categories || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.topCategories && stats.topCategories.length > 0
                ? `${stats.topCategories[0][0]} (${stats.topCategories[0][1]})`
                : 'Nenhuma categoria'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Capacidade Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCapacity || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Vagas disponíveis em todos os cursos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Valor Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.avgCoursePrice || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Preço médio por curso</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Projection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Projeção de Receita
          </CardTitle>
          <CardDescription>Baseada em matrículas simuladas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-600">{formatCurrency(stats?.totalRevenue || 0)}</div>
          <p className="text-sm text-muted-foreground mt-2">
            Receita projetada com base nos cursos ativos
          </p>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Cursos</CardTitle>
            <CardDescription>Gerencie seu catálogo de cursos</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/education/courses">
              <Button className="w-full">
                <BookOpen className="mr-2 h-4 w-4" />
                Gerenciar Cursos
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Receitas</CardTitle>
            <CardDescription>Análise financeira por curso</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/education/revenue">
              <Button className="w-full" variant="outline">
                <DollarSign className="mr-2 h-4 w-4" />
                Ver Relatório
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Matrículas</CardTitle>
            <CardDescription>Gestão de alunos e inscrições</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/education/sales">
              <Button className="w-full" variant="outline">
                <Users className="mr-2 h-4 w-4" />
                Gerenciar Matrículas
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Top Categories */}
      {stats && stats.topCategories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Principais Áreas de Conhecimento</CardTitle>
            <CardDescription>Categorias com mais cursos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topCategories.map(([category, count], index) => (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">#{index + 1}</div>
                    <div>{category}</div>
                  </div>
                  <div className="text-sm text-muted-foreground">{count} cursos</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </MainLayout>
  );
}
